import React from 'react';
import { Button } from 'react-bootstrap';
import { TIMELINE_VIEW_OPTIONS } from './GanttTaskReactChart';

export function TimelineConflictBanner({ banner, onShowWhatToMove }) {
    if (!banner?.text) return null;
    return (
        <div className="tl-conflict-banner" role="status">
            <span className="tl-conflict-icon" aria-hidden>
                ⚠
            </span>
            <p className="tl-conflict-text">
                <strong>Scheduling conflict.</strong> {banner.text}
            </p>
            {onShowWhatToMove && (
                <button type="button" className="tl-conflict-link" onClick={onShowWhatToMove}>
                    Show what to move
                </button>
            )}
        </div>
    );
}

export function TimelineLegendBar({
    view,
    onViewChange,
    onToday,
    refreshing = false,
    extraRight = null,
}) {
    return (
        <div className="tl-legend-bar">
            <div className="tl-legend-swatches">
                <span className="tl-legend-item">
                    <i className="tl-swatch is-scheduled" /> Scheduled
                </span>
                <span className="tl-legend-item">
                    <i className="tl-swatch is-done" /> Done
                </span>
                <span className="tl-legend-item">
                    <i className="tl-swatch is-duesoon" /> Due soon
                </span>
                <span className="tl-legend-item">
                    <i className="tl-swatch is-pastdue" /> Past due
                </span>
                <span className="tl-legend-item">
                    <i className="tl-swatch is-hatch" /> past due
                </span>
                <span className="tl-legend-item">
                    <i className="tl-swatch is-pinned" /> Pinned start
                </span>
                <span className="tl-legend-item">
                    <i className="tl-swatch is-due" /> Due date
                </span>
            </div>
            <div className="tl-legend-right">
                <span className="tl-legend-hint">
                    Drag a bar to move · drag an edge to resize
                    {refreshing ? ' · Updating…' : ''}
                </span>
                <div className="tl-view-toggle" role="tablist" aria-label="Timeline zoom">
                    {TIMELINE_VIEW_OPTIONS.map((opt) => (
                        <button
                            key={opt.id}
                            type="button"
                            role="tab"
                            aria-selected={view === opt.id}
                            className={view === opt.id ? 'is-active' : ''}
                            onClick={() => onViewChange?.(opt.id)}
                        >
                            {opt.label}
                        </button>
                    ))}
                </div>
                <Button variant="outline-secondary" size="sm" className="tl-today-btn" onClick={onToday}>
                    Today
                </Button>
                {extraRight}
            </div>
        </div>
    );
}

export function TimelinePendingBar({
    pendingCount = 0,
    warning = '',
    overCapacityCount = 0,
    onDiscard,
    onApply,
    applying = false,
}) {
    if (!pendingCount) return null;
    return (
        <div className="tl-pending-bar">
            <div className="tl-pending-copy">
                <strong>
                    {pendingCount} pending change{pendingCount === 1 ? '' : 's'}
                </strong>
                <span className="tl-pending-warn">
                    Nothing is saved yet.
                    {overCapacityCount > 0 && (
                        <>
                            {' '}
                            <span className="text-danger">{overCapacityCount} people</span> are over
                            capacity if you apply. Assignees are notified.
                        </>
                    )}
                    {overCapacityCount <= 0 && warning ? ` ${warning}` : null}
                </span>
            </div>
            <div className="tl-pending-actions">
                <Button variant="outline-secondary" size="sm" onClick={onDiscard} disabled={applying}>
                    Discard
                </Button>
                <Button variant="primary" size="sm" onClick={onApply} disabled={applying}>
                    {applying ? 'Applying…' : 'Apply changes'}
                </Button>
            </div>
        </div>
    );
}

export default function TimelinePageChrome({
    title,
    activeCount,
    subtitle,
    extraHeader = null,
    banner = null,
    onShowWhatToMove,
    view,
    onViewChange,
    onToday,
    refreshing = false,
    onPrint,
    onExport,
    pendingCount = 0,
    pendingWarning = '',
    overCapacityCount = 0,
    onDiscard,
    onApply,
    applying = false,
    children,
}) {
    return (
        <div className="tl-page">
            <div className="tl-page-header">
                <div>
                    <h1 className="tl-page-title">
                        {title}
                        {activeCount != null && (
                            <span className="tl-active-pill">{activeCount} active</span>
                        )}
                    </h1>
                    {subtitle ? <p className="tl-page-sub">{subtitle}</p> : null}
                </div>
                <div className="tl-page-actions">
                    {extraHeader}
                    <Button variant="outline-secondary" size="sm" onClick={onPrint}>
                        Print
                    </Button>
                    <Button variant="outline-secondary" size="sm" onClick={onExport}>
                        Export
                    </Button>
                </div>
            </div>
            <TimelineConflictBanner banner={banner} onShowWhatToMove={onShowWhatToMove} />
            <TimelineLegendBar
                view={view}
                onViewChange={onViewChange}
                onToday={onToday}
                refreshing={refreshing}
            />
            <div className="tl-page-body">{children}</div>
            <TimelinePendingBar
                pendingCount={pendingCount}
                warning={pendingWarning}
                overCapacityCount={overCapacityCount}
                onDiscard={onDiscard}
                onApply={onApply}
                applying={applying}
            />
        </div>
    );
}
